import React, { useId, useMemo, useState } from 'react';
import {
  SimulationHotspotInteraction,
  SimulationMovableInteraction,
  SimulationSceneInteraction,
} from '../../../types/v2';
import { ObjectTargetControls } from './ObjectTargetControls';
import {
  SvgDropTargetDefinition,
  SvgPointerDragObject,
} from './SvgPointerDragObject';


interface HardwareWorkbenchPlaceholderSceneProps {
  renderState: Record<string, any>;
  interactions?: SimulationSceneInteraction[];
  disabled?: boolean;
  onAction: (actionId: string) => void;
}


export const HardwareWorkbenchPlaceholderScene: React.FC<HardwareWorkbenchPlaceholderSceneProps> = ({
  renderState,
  interactions = [],
  disabled = false,
  onAction,
}) => {
  const titleId = useId();
  const descriptionId = useId();
  const [localFeedback, setLocalFeedback] = useState('');
  const hotspots = useMemo(() => Object.fromEntries(
    interactions
      .filter((item): item is SimulationHotspotInteraction => item.kind === 'hotspot')
      .map((item) => [item.id, item]),
  ), [interactions]);
  const movables = useMemo(() => Object.fromEntries(
    interactions
      .filter((item): item is SimulationMovableInteraction => item.kind === 'movable')
      .map((item) => [item.id, item]),
  ), [interactions]);

  const powerOn = renderState.power_state === 'on';
  const powerConnected = renderState.power_cable_location === 'connected';
  const screwsRemoved = renderState.screw_state === 'removed';
  const panelRemoved = renderState.panel_location === 'work_surface';
  const clipsOpen = renderState.clip_state === 'open';
  const ramRemoved = renderState.ram_location === 'work_surface';
  const toolInUse = renderState.tool_location === 'in_use';
  const ramInteraction = movables.ram_module;
  const ramTargets: SvgDropTargetDefinition[] = ramInteraction
    ? ramInteraction.targets
      .filter((target) => target.state_value !== renderState.ram_location)
      .map((target) => {
        if (target.id === 'memory_slot') {
          return {
            id: target.id,
            label: target.label,
            actionId: target.action_id,
            x: 165,
            y: 145,
            width: 210,
            height: 125,
          };
        }
        return {
          id: target.id,
          label: target.label,
          actionId: target.action_id,
          x: 555,
          y: 245,
          width: 165,
          height: 105,
        };
      })
    : [];
  const description = [
    `The computer is powered ${powerOn ? 'on' : 'off'}.`,
    `The power cable is ${powerConnected ? 'connected' : 'disconnected'}.`,
    `The case screws are ${screwsRemoved ? 'removed' : 'installed'}.`,
    `The side panel is ${panelRemoved ? 'on the work surface' : 'attached to the case'}.`,
    `The RAM clips are ${clipsOpen ? 'open' : 'closed'}.`,
    `The RAM module is ${ramRemoved ? 'on the antistatic work surface' : 'in the memory slot'}.`,
    `The screwdriver is ${toolInUse ? 'at the case screws' : 'in the tool rack'}.`,
  ].join(' ');
  const submitHotspot = (id: string) => {
    const interaction = hotspots[id];
    if (!interaction || disabled) return;
    onAction(interaction.action_id);
  };

  return (
    <div className="simulation-scene simulation-hardware-scene" aria-label="Interactive placeholder hardware workbench">
      <svg viewBox="0 0 800 440" role="img" aria-labelledby={titleId} aria-describedby={descriptionId}>
        <title id={titleId}>Desktop computer hardware workbench</title>
        <desc id={descriptionId}>{description}</desc>
        <rect x="18" y="18" width="764" height="404" rx="18" className="simulation-room" />
        <rect x="70" y="64" width="410" height="310" rx="14" className="simulation-case-shell" />
        <rect x="98" y="92" width="354" height="254" rx="10" className="simulation-case-interior" />
        {!panelRemoved ? (
          <rect x="78" y="72" width="394" height="294" rx="12" className="simulation-side-panel" />
        ) : null}
        {!screwsRemoved ? (
          <>
            <circle cx="92" cy="87" r="7" className="simulation-screw" />
            <circle cx="458" cy="87" r="7" className="simulation-screw" />
            <circle cx="92" cy="351" r="7" className="simulation-screw" />
            <circle cx="458" cy="351" r="7" className="simulation-screw" />
          </>
        ) : null}
        {panelRemoved ? (
          <>
            <rect x="175" y="183" width="190" height="48" rx="5" className="simulation-motherboard-slot" />
            <rect x="199" y="176" width="12" height="62" className={clipsOpen ? 'simulation-ram-clip-open' : 'simulation-ram-clip'} />
            <rect x="329" y="176" width="12" height="62" className={clipsOpen ? 'simulation-ram-clip-open' : 'simulation-ram-clip'} />
          </>
        ) : null}
        <circle cx="425" cy="105" r="10" className={powerOn ? 'simulation-led-on' : 'simulation-led-off'} />
        <rect x="535" y="68" width="205" height="306" rx="12" className="simulation-work-surface" />
        <text x="637" y="98" textAnchor="middle" className="simulation-work-label">ANTISTATIC WORK AREA</text>
        {panelRemoved ? (
          <rect x="555" y="122" width="165" height="105" rx="9" className="simulation-side-panel" />
        ) : null}
        {panelRemoved && ramInteraction ? (
          <SvgPointerDragObject
            id={ramInteraction.id}
            label={ramInteraction.label}
            x={ramRemoved ? 584 : 216}
            y={ramRemoved ? 275 : 183}
            width={108}
            height={48}
            targets={ramTargets}
            disabled={disabled}
            onDrop={(actionId) => {
              setLocalFeedback('');
              onAction(actionId);
            }}
            onInvalidDrop={() => setLocalFeedback(
              'The RAM module returned to its original position. Choose a highlighted destination.',
            )}
          >
            <rect x="0" y="0" width="108" height="48" rx="4" className="simulation-ram-module" />
            <rect x="10" y="8" width="14" height="23" className="simulation-ram-chip" />
            <rect x="34" y="8" width="14" height="23" className="simulation-ram-chip" />
            <rect x="58" y="8" width="14" height="23" className="simulation-ram-chip" />
            <rect x="82" y="8" width="14" height="23" className="simulation-ram-chip" />
          </SvgPointerDragObject>
        ) : null}
        <g transform={toolInUse ? 'translate(50 270)' : 'translate(625 340)'}>
          <rect x="0" y="0" width="76" height="12" rx="6" className="simulation-tool-handle" />
          <rect x="72" y="4" width="45" height="4" className="simulation-tool-shaft" />
        </g>
        <path
          d={powerConnected ? 'M70 315 C35 315, 36 390, 116 390' : 'M70 315 C42 315, 45 350, 112 350'}
          className="simulation-power-cable"
        />
        <rect x="105" y={powerConnected ? 380 : 340} width="32" height="20" rx="3" className="simulation-cable-end" />
      </svg>

      {hotspots.workstation ? (
        <button type="button" className="simulation-hotspot hardware-hotspot-inspect" disabled={disabled} onClick={() => submitHotspot('workstation')}>
          Inspect
        </button>
      ) : null}
      {powerOn && hotspots.power_off_control ? (
        <button type="button" className="simulation-hotspot hardware-hotspot-power" disabled={disabled} onClick={() => submitHotspot('power_off_control')}>
          Power off
        </button>
      ) : null}
      {!powerOn && hotspots.power_on_control ? (
        <button type="button" className="simulation-hotspot hardware-hotspot-power" disabled={disabled} onClick={() => submitHotspot('power_on_control')}>
          Power on
        </button>
      ) : null}
      {hotspots.case_screws && !screwsRemoved ? (
        <button type="button" className="simulation-hotspot hardware-hotspot-screws" disabled={disabled} onClick={() => submitHotspot('case_screws')}>
          Screws
        </button>
      ) : null}
      {panelRemoved && !clipsOpen && hotspots.ram_clips_open ? (
        <button type="button" className="simulation-hotspot hardware-hotspot-clips" disabled={disabled} onClick={() => submitHotspot('ram_clips_open')}>
          Open clips
        </button>
      ) : null}
      {panelRemoved && clipsOpen && hotspots.ram_clips_close ? (
        <button type="button" className="simulation-hotspot hardware-hotspot-clips" disabled={disabled} onClick={() => submitHotspot('ram_clips_close')}>
          Close clips
        </button>
      ) : null}
      {hotspots.verify_control ? (
        <button type="button" className="simulation-hotspot hardware-hotspot-verify" disabled={disabled} onClick={() => submitHotspot('verify_control')}>
          Verify
        </button>
      ) : null}

      <div className="simulation-hardware-movables" aria-label="Movable hardware">
        {Object.values(movables).map((interaction) => (
          <ObjectTargetControls
            key={interaction.id}
            interaction={interaction}
            currentValue={renderState[interaction.state_field]}
            disabled={disabled}
            onAction={onAction}
          />
        ))}
      </div>
      {localFeedback ? (
        <p className="simulation-local-feedback" role="status" aria-live="polite">
          {localFeedback}
        </p>
      ) : null}
    </div>
  );
};
